import type { JSX, ReactNode } from 'react';
import { ChipCount } from '../../components/ChipCount.js';
import { FullscreenButton } from '../../components/FullscreenButton.js';
import { Icon } from '../../components/Icon.js';
import { useT } from '../../i18n/index.js';
import { useApp } from '../../state/store.js';
import './screen.css';

/**
 * The shell every non-Compound screen wears: back to the compound, the
 * persistent wallet count, and one-tap reach to the store, the wallet and
 * responsible play. §13 requires responsible play to be reachable from every
 * store surface, so it lives in the rail rather than at the bottom of a page.
 */

interface ScreenProps {
  title: string;
  sub?: string;
  /** Where "back" walks to. Defaults to the compound. */
  onBack?: () => void;
  children: ReactNode;
}

export function Screen({ title, sub, onBack, children }: ScreenProps): JSX.Element {
  const go = useApp((s) => s.go);
  const balance = useApp((s) => s.balance);
  const t = useT();

  return (
    <div className="s-screen">
      <header className="s-rail">
        <button className="s-rail__back" onClick={onBack ?? (() => go({ s: 'compound' }))}>
          {t('table.back')}
        </button>
        <div className="s-rail__title">
          <b>{title}</b>
          {sub ? <span>{sub}</span> : null}
        </div>
        <div className="s-rail__spacer" />
        <button className="s-rail__wallet" onClick={() => go({ s: 'wallet' })}>
          <ChipCount value={balance} compact />
        </button>
        <FullscreenButton />
        <button className="s-iconbtn" onClick={() => go({ s: 'store' })} title={t('table.store')}>
          <Icon name="tindahan" size={22} label={t('table.store')} />
        </button>
        <button
          className="s-iconbtn"
          onClick={() => go({ s: 'settings' })}
          title={t('table.settings')}
        >
          <Icon name="responsible" size={22} label={t('table.settings.label')} />
        </button>
      </header>
      <div className="s-body">{children}</div>
    </div>
  );
}

/** The one disclosure string, rendered the same way on every surface. */
export function Disclosure({ text }: { text: string }): JSX.Element {
  return (
    <p className="s-disclosure">
      <Icon name="chip" size={16} />
      <span>{text}</span>
    </p>
  );
}
